import { Component, OnDestroy, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatStateService } from '../../../../services/chat-state.service';
import { TextareaInputComponent } from '../../form/textarea-input/textarea-input.component';
import { getSendIconSrc } from '../../../../utils/dock-resize.util';

const DRAG_THRESHOLD = 5;

const ICON_BOX = 60;

@Component({
  selector: 'app-chat-entry-bar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextareaInputComponent, MatTooltipModule],
  templateUrl: './chat-entry-bar.component.html',
  styleUrl: './chat-entry-bar.component.scss',
})
export class ChatEntryBarComponent implements OnInit, OnDestroy {
  readonly getSendIconSrc = getSendIconSrc;
  queryControl = new FormControl('', [Validators.maxLength(1000)]);
  isDockOpen = false;
  isBarMinimized = true;

  iconX = 0;
  iconY = 0;

  isPositioned = false;

  isDragging = false;

  private readonly DRAG_POS_KEY = 'chatbot-drag-position';

  private _pointerActive = false;
  private _startClientX = 0;
  private _startClientY = 0;
  private _startIconX = 0;
  private _startIconY = 0;
  private _movedBeyondThreshold = false;

  private dockSub!: Subscription;
  private _resizeHandler!: () => void;

  constructor(
    public chatState: ChatStateService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    this.dockSub = this.chatState.isDockOpen$.subscribe((open) => {
      this.isDockOpen = open;
    });

    this.queryControl.valueChanges.subscribe((value) => {
      if (!value) return;
      if (value.length > 1000) {
        this.queryControl.setValue(value.slice(0, 1000), { emitEvent: false });
      }
    });

    this.initPosition();

    if (isPlatformBrowser(this.platformId)) {
      this._resizeHandler = () => this.onWindowResize();
      window.addEventListener('resize', this._resizeHandler, { passive: true });
    }
  }


  private initPosition(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const saved = localStorage.getItem(this.DRAG_POS_KEY);
    if (saved) {
      try {
        const pos = JSON.parse(saved) as { x: number; y: number };
        this.iconX = Math.max(0, Math.min(pos.x, window.innerWidth - ICON_BOX));
        this.iconY = Math.max(0, Math.min(pos.y, window.innerHeight - ICON_BOX));
        this.isPositioned = true;
        return;
      } catch {
        /* fall through to default */
      }
    }
    // Default: bottom-right corner
    this.iconX = window.innerWidth - ICON_BOX - 24;
    this.iconY = window.innerHeight - ICON_BOX - 24;
    this.isPositioned = true;
  }

  private savePosition(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.DRAG_POS_KEY, JSON.stringify({ x: this.iconX, y: this.iconY }));
  }

  private onWindowResize(): void {
    const maxX = window.innerWidth - ICON_BOX;
    const maxY = window.innerHeight - ICON_BOX;
    const clampedX = Math.max(0, Math.min(this.iconX, maxX));
    const clampedY = Math.max(0, Math.min(this.iconY, maxY));

    if (clampedX !== this.iconX || clampedY !== this.iconY) {
      this.iconX = clampedX;
      this.iconY = clampedY;
      this.savePosition();
    }
  }

  onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;  // left-click / primary touch only

    this._pointerActive = true;
    this._movedBeyondThreshold = false;
    this._startClientX = e.clientX;
    this._startClientY = e.clientY;
    this._startIconX = this.iconX;
    this._startIconY = this.iconY;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  onPointerMove(e: PointerEvent): void {
    if (!this._pointerActive) return;

    const dx = e.clientX - this._startClientX;
    const dy = e.clientY - this._startClientY;

    if (!this._movedBeyondThreshold) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      this._movedBeyondThreshold = true;
    }

    this.iconX = Math.max(0, Math.min(this._startIconX + dx, window.innerWidth - ICON_BOX));
    this.iconY = Math.max(0, Math.min(this._startIconY + dy, window.innerHeight - ICON_BOX));
  }

  onPointerUp(e: PointerEvent): void {
    if (!this._pointerActive) return;
    this._pointerActive = false;

    if (this._movedBeyondThreshold) {
      // Real drag — save new position
      this.isDragging = true;
      this.savePosition();
      setTimeout(() => { this.isDragging = false; }, 0);
    } else {
      // Pure tap — open the bar at its fixed bottom-right position
      this.openBar();
    }
  }

  // ─── Bar open / close ─────────────────────────────────────────────────────

  closeBar(): void {
    this.isBarMinimized = true;
  }

  openBar(): void {
    if (this.isDragging) return;
    // Bar is always anchored at bottom-right via CSS — no position clamping needed
    this.isBarMinimized = false;
  }

  onBarClick(): void {
    if (this.chatState.messages.length > 0) {
      this.chatState.openDock();
    }
  }

  // ─── Form handlers ────────────────────────────────────────────────────────

  onEnterKeyFn = (e: Event): void => {
    if ((e as KeyboardEvent).key !== 'Enter') return;
    if ((e as KeyboardEvent).shiftKey) return;
    e.preventDefault();
    this.submit();
  };

  submit(): void {
    const text = this.queryControl.value?.trim();
    if (!text || this.queryControl.invalid) return;
    this.chatState.openDock(text);
    this.queryControl.reset('');
  }

  ngOnDestroy(): void {
    this.dockSub?.unsubscribe();
    if (isPlatformBrowser(this.platformId) && this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }
}
